import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle, ExternalLink, ChevronDown, Package, Users, BarChart3, ClipboardList, Truck } from 'lucide-react';
import clsx from 'clsx';
import { Card } from '../shared/components/Card.js';
import { Button } from '../shared/components/Button.js';
import { useToast } from '../shared/components/Toast.js';
import { apiFetch } from '../shared/hooks/useApi.js';
import { formatDatumTijd } from '../shared/utils/formatters.js';
import { useImportWinkel, ImportWinkelBanner } from '../shared/components/ImportWinkelGuard.js';
import { WinkelLabel } from '../shared/components/WinkelLabel.js';

// === Types ===

interface ParseResponse {
  documentType: string;
  parsed: any;
  filename: string;
  storedAs: string;
  /** Winkel uit het document (null = niet herkend) */
  winkelUitDocument?: { entityId: string; naam: string; kleur: string } | null;
  /** Gezet als het document duidelijk bij een andere winkel hoort — importeren wordt dan geweigerd */
  andereWinkel?: { message: string } | null;
}

interface ImportLogEntry {
  id: string;
  bron: string;
  bestandsnaam: string | null;
  documentNr: string | null;
  documentType: string | null;
  bestelbonNr: string | null;
  status: 'SUCCES' | 'FOUT' | 'DUPLICAAT' | 'HANDMATIG_AANGEPAST';
  payload: any;
  resultaat: any;
  error: string | null;
  dossierId: string | null;
  createdAt: string;
}

const documentTypeLabels: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  offerte: { label: 'Offerte', icon: <FileText size={16} />, color: 'text-blue-600 bg-blue-50' },
  bestelbon: { label: 'Bestelbon', icon: <Package size={16} />, color: 'text-green-600 bg-green-50' },
  contramerk: { label: 'Contramerk', icon: <Truck size={16} />, color: 'text-purple-600 bg-purple-50' },
  voorbereiding_contramerk: { label: 'Voorb. Contramerk', icon: <ClipboardList size={16} />, color: 'text-orange-600 bg-orange-50' },
  plaatsingsdossier: { label: 'Plaatsingsdossier', icon: <ClipboardList size={16} />, color: 'text-teal-600 bg-teal-50' },
  concretisatie: { label: 'Concretisatie', icon: <BarChart3 size={16} />, color: 'text-indigo-600 bg-indigo-50' },
  bezoekers: { label: 'Bezoekers', icon: <Users size={16} />, color: 'text-pink-600 bg-pink-50' },
  omzet: { label: 'Omzet', icon: <BarChart3 size={16} />, color: 'text-amber-600 bg-amber-50' },
  unknown: { label: 'Onbekend', icon: <AlertTriangle size={16} />, color: 'text-gray-600 bg-gray-50' },
};

// === Main Component ===

export function ImportPage() {
  const winkel = useImportWinkel();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [parseResult, setParseResult] = useState<ParseResponse | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // === Import logs ===
  const { data: logs } = useQuery({
    queryKey: ['import-logs'],
    queryFn: () => apiFetch<ImportLogEntry[]>('/api/import/logs'),
  });

  // === Parse mutation ===
  const parseMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/import/smart/parse', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Upload mislukt');
      }
      return res.json() as Promise<ParseResponse>;
    },
    onSuccess: (data) => {
      setParseResult(data);
      const typeInfo = documentTypeLabels[data.documentType] || documentTypeLabels.unknown;
      toast('success', `${typeInfo.label} gedetecteerd — controleer de data en importeer`);
    },
    onError: (err: Error) => {
      toast('error', err.message);
    },
  });

  // === Create import mutation ===
  const createMutation = useMutation({
    mutationFn: async (storedAs: string) => {
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/import/smart/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ storedAs }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Import mislukt');
      }
      return res.json();
    },
    onSuccess: (data) => {
      const msg = data.message || 'Import succesvol';
      toast('success', msg);
      queryClient.invalidateQueries({ queryKey: ['import-logs'] });
      setParseResult(null);
      if (data.createdEntities?.dossier) {
        navigate(`/dossiers/${data.createdEntities.dossier}`);
      }
    },
    onError: (err: Error) => {
      toast('error', err.message);
    },
  });

  // === Drag & drop handlers ===
  const handleFile = useCallback((file: File) => {
    if (!winkel.isBevestigd()) { toast('error', `Bevestig eerst bovenaan dat je in ${winkel.winkelNaam} importeert`); return; }
    if (file.type !== 'application/pdf') {
      toast('error', 'Alleen PDF bestanden toegestaan');
      return;
    }
    parseMutation.mutate(file);
  }, [parseMutation, toast]);

  const onDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); }, []);
  const onDragLeave = useCallback(() => setIsDragging(false), []);
  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">SMART PDF Import</h1>
        <p className="text-gray-500 mt-1">Importeer documenten uit SMART software (offertes, bestelbonnen, contramerken, plaatsingsdossiers, rapporten)</p>
      </div>

      <ImportWinkelBanner guard={winkel} />
      {/* Upload Zone */}
      {!parseResult && (
        <Card>
          <div
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={clsx(
              'border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors',
              isDragging ? 'border-gold bg-gold/5' : 'border-gray-300 hover:border-gold/50 hover:bg-gray-50',
              parseMutation.isPending && 'pointer-events-none opacity-60'
            )}
          >
            {parseMutation.isPending ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-gray-600">PDF wordt geanalyseerd...</p>
              </div>
            ) : (
              <>
                <Upload size={40} className="mx-auto text-gray-400 mb-3" />
                <p className="text-lg font-medium text-gray-700">Sleep een SMART PDF hierheen</p>
                <p className="text-sm text-gray-500 mt-1">of klik om te selecteren</p>
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {Object.entries(documentTypeLabels).filter(([k]) => k !== 'unknown').map(([key, { label, icon, color }]) => (
                    <span key={key} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${color}`}>
                      {icon} {label}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-3">Alleen .pdf bestanden, max 20MB</p>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              e.target.value = '';
            }}
          />
        </Card>
      )}

      {/* Preview & Import */}
      {parseResult && (
        <PreviewPanel
          result={parseResult}
          onImport={() => createMutation.mutate(parseResult.storedAs)}
          onCancel={() => setParseResult(null)}
          isLoading={createMutation.isPending}
        />
      )}

      {/* Import Logs */}
      <ImportLogTable logs={logs || []} navigate={navigate} />
    </div>
  );
}

// === Preview Panel ===

function PreviewPanel({
  result, onImport, onCancel, isLoading,
}: {
  result: ParseResponse;
  onImport: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const typeInfo = documentTypeLabels[result.documentType] || documentTypeLabels.unknown;
  const parsed = result.parsed;

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full ${typeInfo.color}`}>
              {typeInfo.icon} {typeInfo.label}
            </span>
            <span className="text-sm text-gray-500">{result.filename}</span>
            {result.winkelUitDocument && <WinkelLabel naam={result.winkelUitDocument.naam} kleur={result.winkelUitDocument.kleur} />}
          </div>
          <Button variant="ghost" size="sm" onClick={onCancel}>Annuleren</Button>
        </div>

        {result.andereWinkel && (
          <div className="mb-4 text-sm rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 flex items-center gap-2">
            <AlertTriangle size={14} className="flex-shrink-0" /> {result.andereWinkel.message}
          </div>
        )}

        {/* Document-specific preview */}
        {parsed && (
          <div className="space-y-4">
            {/* Klant info (shared across most types) */}
            {parsed.klant && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Klantgegevens</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <InfoField label="Naam" value={parsed.klant.naam} />
                  <InfoField label="Adres" value={parsed.klant.adresStraat} />
                  <InfoField label="Postcode + Gemeente" value={`${parsed.klant.postcode} ${parsed.klant.gemeente}`} />
                  {parsed.klant.email && <InfoField label="Email" value={parsed.klant.email} />}
                  {parsed.klant.telefoon1 && <InfoField label="Telefoon" value={parsed.klant.telefoon1} />}
                </div>
              </div>
            )}

            {/* Document numbers */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {parsed.bestelbonNr && <InfoField label="Bestelbon Nr" value={parsed.bestelbonNr} highlight />}
              {parsed.offerteNr && <InfoField label="Offerte Nr" value={parsed.offerteNr} highlight />}
              {parsed.offerteRef && <InfoField label="Offerte Ref" value={parsed.offerteRef} />}
              {parsed.leveringsdatum && <InfoField label="Leveringsdatum" value={parsed.leveringsdatum} />}
              {parsed.leveringsdatumDef && <InfoField label="Leveringsdatum" value={parsed.leveringsdatumDef} />}
              {parsed.plaatsingsdatumStart && <InfoField label="Plaatsingsdatum" value={`${parsed.plaatsingsdatumStart}${parsed.plaatsingsdatumEind ? ` - ${parsed.plaatsingsdatumEind}` : ''}`} />}
              {parsed.verkoper?.naam && <InfoField label="Verkoper" value={parsed.verkoper.naam} />}
              {parsed.plaatser && <InfoField label="Plaatser" value={parsed.plaatser} />}
              {parsed.leverancier && <InfoField label="Leverancier" value={parsed.leverancier} />}
            </div>

            {/* Keuken configuratie */}
            {parsed.keukenConfiguratie && Object.keys(parsed.keukenConfiguratie).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Keuken Configuratie</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {parsed.keukenConfiguratie.model && <InfoField label="Model" value={parsed.keukenConfiguratie.model} />}
                  {parsed.keukenConfiguratie.front && <InfoField label="Front" value={parsed.keukenConfiguratie.front} />}
                  {parsed.keukenConfiguratie.werkbladuitvoering && <InfoField label="Werkblad" value={parsed.keukenConfiguratie.werkbladuitvoering} />}
                </div>
              </div>
            )}

            {/* Artikelen count */}
            {parsed.artikelen && parsed.artikelen.length > 0 && (
              <div className="text-sm text-gray-600">
                {parsed.artikelen.length} artikelen gevonden
              </div>
            )}

            {/* Items count (contramerk/voorbereiding) */}
            {parsed.items && parsed.items.length > 0 && (
              <div className="text-sm text-gray-600">
                {parsed.items.length} items gevonden
                {parsed.items.filter && parsed.items.some((i: any) => i.ok !== undefined) && (
                  <span className="ml-2">
                    ({parsed.items.filter((i: any) => i.ok).length}/{parsed.items.length} OK)
                  </span>
                )}
              </div>
            )}

            {/* BTW onderverdeling */}
            {parsed.btwOnderverdeling && parsed.btwOnderverdeling.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">BTW Onderverdeling</h3>
                <div className="grid grid-cols-3 gap-2">
                  {parsed.btwOnderverdeling.map((btw: any, i: number) => (
                    <div key={i} className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                      {btw.tarief}%: {btw.inclBtw?.toFixed(2)} incl
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Totalen */}
            {(parsed.totaalInclBtw || parsed.totaalExclBtw) && (
              <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-200">
                {parsed.totaalExclBtw > 0 && <InfoField label="Excl. BTW" value={`${parsed.totaalExclBtw?.toFixed(2)} EUR`} />}
                {parsed.totaalBtw > 0 && <InfoField label="BTW" value={`${parsed.totaalBtw?.toFixed(2)} EUR`} />}
                {parsed.totaalInclBtw > 0 && <InfoField label="Incl. BTW" value={`${parsed.totaalInclBtw?.toFixed(2)} EUR`} highlight />}
              </div>
            )}

            {/* Betalingen */}
            {parsed.betalingen && parsed.betalingen.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Betalingen</h3>
                {parsed.betalingen.map((b: any, i: number) => (
                  <div key={i} className="text-xs text-gray-600">
                    {b.methode} op {b.datum}: {b.bedrag?.toFixed(2)} EUR
                  </div>
                ))}
                {parsed.openstaandSaldo > 0 && (
                  <div className="text-xs font-medium text-red-600 mt-1">
                    Openstaand saldo: {parsed.openstaandSaldo?.toFixed(2)} EUR
                  </div>
                )}
              </div>
            )}

            {/* Dashboard rapport specific */}
            {parsed.type === 'concretisatie' && parsed.verkopers && (
              <div className="text-sm text-gray-600">
                {parsed.verkopers.length} verkopers — {parsed.totaal?.projecten} projecten, {parsed.totaal?.verkopen} verkopen
              </div>
            )}
            {parsed.type === 'bezoekers' && parsed.totaal && (
              <div className="text-sm text-gray-600">
                {parsed.maanden?.length} maanden — {parsed.totaal.bezoeken} bezoeken, {parsed.totaal.projecten} projecten
              </div>
            )}
            {parsed.type === 'omzet' && parsed.jaren && (
              <div className="text-sm text-gray-600">
                {parsed.jaren.length} jaar(en) — Basis: {parsed.basis}
              </div>
            )}
          </div>
        )}

        {result.documentType === 'unknown' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
            Dit documenttype kon niet automatisch herkend worden. Controleer of het een geldig SMART PDF document is.
          </div>
        )}

        {/* Import button */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-4">
          <Button variant="secondary" onClick={onCancel}>Annuleren</Button>
          <Button
            onClick={onImport}
            disabled={isLoading || result.documentType === 'unknown' || !!result.andereWinkel}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Importeren...
              </>
            ) : (
              <>
                <FileText size={16} />
                Importeer {typeInfo.label}
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// === Info Field ===

function InfoField({ label, value, highlight }: { label: string; value: string | undefined; highlight?: boolean }) {
  if (!value) return null;
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
      <p className={clsx('text-sm', highlight ? 'font-semibold text-gray-900' : 'text-gray-700')}>{value}</p>
    </div>
  );
}

// === Import Log Table ===

const statusConfig: Record<string, { bg: string; text: string; label: string; icon: React.ReactNode }> = {
  SUCCES: { bg: 'bg-green-100', text: 'text-green-700', label: 'Succes', icon: <CheckCircle size={12} /> },
  FOUT: { bg: 'bg-red-100', text: 'text-red-700', label: 'Fout', icon: <XCircle size={12} /> },
  DUPLICAAT: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Duplicaat', icon: <AlertTriangle size={12} /> },
  HANDMATIG_AANGEPAST: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Aangepast', icon: <FileText size={12} /> },
};

function ImportLogTable({ logs, navigate }: { logs: ImportLogEntry[]; navigate: ReturnType<typeof import('react-router-dom').useNavigate> }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (logs.length === 0) return null;

  return (
    <Card padding={false}>
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700">Recente imports</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Datum</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Type</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Bestand</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Document nr</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
              <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Actie</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(log => {
              const cfg = statusConfig[log.status] || statusConfig.FOUT;
              const typeInfo = documentTypeLabels[log.documentType || ''] || null;
              const isExpanded = expandedId === log.id;

              return (
                <tr key={log.id} className="border-b border-gray-100">
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDatumTijd(log.createdAt)}</td>
                  <td className="px-4 py-3">
                    {typeInfo ? (
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${typeInfo.color}`}>
                        {typeInfo.icon} {typeInfo.label}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">{log.bron}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700 max-w-[200px] truncate">{log.bestandsnaam || '—'}</td>
                  <td className="px-4 py-3 text-gray-900 font-medium">{log.bestelbonNr || log.documentNr || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${cfg.bg} ${cfg.text}`}>
                      {cfg.icon} {cfg.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {log.status === 'SUCCES' && log.dossierId && (
                      <button
                        onClick={() => navigate(`/dossiers/${log.dossierId}`)}
                        className="text-xs font-medium text-gold hover:text-gold-dark inline-flex items-center gap-1"
                      >
                        <ExternalLink size={12} /> Dossier
                      </button>
                    )}
                    {log.status === 'FOUT' && log.error && (
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : log.id)}
                        className="text-xs font-medium text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                      >
                        <ChevronDown size={12} className={clsx('transition-transform', isExpanded && 'rotate-180')} />
                        Details
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Expanded error details */}
      {expandedId && (() => {
        const log = logs.find(l => l.id === expandedId);
        if (!log?.error) return null;
        return (
          <div className="px-6 py-3 bg-red-50 border-t border-red-200 text-sm text-red-700">
            <strong>Error:</strong> {log.error}
          </div>
        );
      })()}
    </Card>
  );
}
